import React, { useState } from 'react';
import { ArrowLeft, Clock, Calendar, Share2, Copy, Check, Twitter, Facebook, Edit3, Trash2, Loader2 } from 'lucide-react';
import { motion } from 'motion/react';
import { format } from 'date-fns';

const AUTHOR_ME = {
  name: 'Amelia Downie',
  role: 'Land Healing Practitioner',
  bio: 'Specializing in property mapping, soil biology restoration, and bio-frequency tuning to create harmonious landscapes.',
  avatar: null, // Fallback to initial
};

export default function BlogDetail({
  post,
  isAdmin,
  onBack,
  onEdit,
  onDelete,
}) {
  const [copied, setCopied] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    setIsDeleting(true);
    try {
      await onDelete(id);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const id = post._id;
  const dateStr = post.createdAt ? format(new Date(post.createdAt), 'MMM d, yyyy') : '';

  return (
    <motion.article
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -15 }}
      transition={{ duration: 0.4 }}
      className="max-w-4xl mx-auto py-10 px-4 md:px-8"
      id={`blog-detail-${id}`}
    >
      <div className="flex items-center justify-between mb-8 pb-3 border-b border-gray-100">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-500 hover:text-brand-green-dark transition-colors font-medium text-sm cursor-pointer group"
          id="btn-detail-back"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to list
        </button>

        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onEdit(post)}
              className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold bg-brand-cream hover:bg-brand-gold/10 text-brand-green-dark border border-gray-200 hover:border-brand-gold/30 rounded-lg transition-all cursor-pointer"
            >
              <Edit3 className="w-3.5 h-3.5 text-brand-green-medium" />
              Edit Article
            </button>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-semibold bg-white hover:bg-red-50 text-red-600 border border-red-100 hover:border-red-200 rounded-lg transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              {isDeleting ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        )}
      </div>

      <header className="mb-8" id="article-header">
        <div className="flex flex-wrap gap-2 mb-4">
          {(post.tags || []).map((tag) => (
            <span
              key={tag}
              className="text-[10px] uppercase tracking-wider bg-brand-gold/10 text-brand-gold-dark border border-brand-gold/20 font-bold px-3 py-1 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>

        <h1 className="font-serif text-3xl md:text-5xl text-brand-green-dark leading-tight font-medium mb-4">
          {post.title}
        </h1>

        {post.subtitle && (
          <p className="text-gray-500 text-base md:text-lg italic leading-relaxed mb-6 font-light">
            {post.subtitle}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-4 py-4 border-y border-gray-100">
          <div className="flex items-center gap-3">
            {AUTHOR_ME.avatar ? (
              <img
                src={AUTHOR_ME.avatar}
                alt={AUTHOR_ME.name}
                className="w-11 h-11 rounded-full object-cover border-2 border-brand-gold/40"
              />
            ) : (
              <div className="w-11 h-11 rounded-full bg-brand-cream border border-brand-gold/30 flex items-center justify-center">
                <span className="text-brand-gold font-bold text-sm">AD</span>
              </div>
            )}
            <div>
              <p className="text-sm font-semibold text-brand-green-dark">
                By {AUTHOR_ME.name}
              </p>
              <p className="text-xs text-brand-green-light font-light uppercase tracking-wider">
                {AUTHOR_ME.role}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-5 text-xs text-gray-400 font-medium tracking-wide">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-brand-gold" />
              {dateStr}
            </span>
            {post.readTime && (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-gray-300"></span>
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-brand-gold" />
                  {post.readTime}
                </span>
              </>
            )}
          </div>
        </div>
      </header>

      {post.coverImage && (
        <div className="w-full h-80 md:h-120 rounded-2xl overflow-hidden mb-10 shadow-md border border-gray-100">
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div 
        className="prose max-w-none text-gray-700 leading-relaxed mb-12 select-text"
        dangerouslySetInnerHTML={{ __html: post.content }}
        id="article-content"
      />

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-5 border-y border-gray-100 mb-12">
        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400 flex items-center gap-2">
          <Share2 className="w-3.5 h-3.5 text-brand-gold-dark" />
          Share this article
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}`, '_blank')}
            className="p-2 bg-gray-50 hover:bg-brand-gold/5 border border-gray-200 hover:border-brand-gold/30 text-gray-500 hover:text-brand-green-dark rounded-full transition-colors cursor-pointer"
            title="Share on X"
          >
            <Twitter className="w-4 h-4" />
          </button>
          <button
            onClick={() => window.open(`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
            className="p-2 bg-gray-50 hover:bg-brand-gold/5 border border-gray-200 hover:border-brand-gold/30 text-gray-500 hover:text-brand-green-dark rounded-full transition-colors cursor-pointer"
            title="Share on Facebook"
          >
            <Facebook className="w-4 h-4" />
          </button>
          <button
            onClick={handleCopyLink}
            className="flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-brand-gold/5 border border-gray-200 hover:border-brand-gold/30 text-gray-500 hover:text-brand-green-dark rounded-full transition-all text-xs font-medium cursor-pointer"
            title="Copy URL"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-brand-green-medium" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                Copy Link
              </>
            )}
          </button>
        </div>
      </div>

      <div className="bg-brand-cream/60 rounded-2xl border border-brand-gold/15 p-6 md:p-8 flex flex-col md:flex-row items-center md:items-start gap-6 shadow-sm">
        <div className="w-20 h-20 rounded-full bg-brand-cream flex items-center justify-center border-2 border-brand-gold/50 shadow-sm shrink-0">
          <span className="text-brand-gold font-bold text-2xl">AD</span>
        </div>
        <div className="flex-grow text-center md:text-left flex flex-col gap-2">
          <h4 className="font-serif text-lg md:text-xl text-brand-green-dark font-medium">
            About {AUTHOR_ME.name}
          </h4>
          <p className="text-xs text-brand-gold-dark font-sans font-semibold tracking-wider uppercase">
            {AUTHOR_ME.role}
          </p>
          <p className="text-sm text-gray-500 leading-relaxed font-light">
            {AUTHOR_ME.bio}
          </p>
        </div>
      </div>
    </motion.article>
  );
}
